import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { CampaignStatusIcon } from '@/components/CampaignSelect';
import { formatCampaignDateRange } from '@/lib/utils/campaignMeta';
import { CAMPAIGN_STATUS_DOT_CLASS } from '@/lib/utils/campaignStatusStyle';
import Tooltip from '@/components/Tooltip';
import { ChannelThumbnail } from '@/components/ChannelThumbnail';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import type { UserChannel } from '@/lib/utils/channelPlatformMeta';
import { useI18nText } from '@/lib/utils/i18nText';
import { useAppDispatch } from '@/store/hooks';
import { showSnackbar } from '@/store/slices/snackbarSlice';
import { loadPanelUser } from '@/lib/api/panel.api';
import { getCampaigns } from '@/lib/api/campaign.api';
import { useCornerResize } from '@/hooks/useCornerResize';
import { PopoverResizeHandle } from '@/components/PopoverResizeHandle';

export interface CampaignHistoryItem {
  id: string;
  name: string;
  status?: string;
  publicationDates?: string[];
  unmatched?: boolean;
  unmatchedTooltip?: string;
}

/** Whose history this popover is showing — rendered in the header exactly like
 *  EstPriceCell's "Details" popover (thumbnail + platform badge + name), so the
 *  two channel-row popovers share one visual identity. Optional: callers that
 *  can't supply it without an extra fetch (e.g. a bare `items` list built from
 *  data that doesn't include the channel) simply get a header with no identity. */
export interface CampaignHistoryChannelInfo {
  thumbnail?: string;
  /** Plain string (not `UserChannel['type']`) so callers can pass a loosely-typed
   *  `channelType` field (e.g. `HistoryRow.channelType`) straight through — matches
   *  `ChannelTypeIcon`'s own `type: string` prop, which this ultimately feeds. */
  type?: string;
  name?: string;
}

interface CampaignHistoryCampaignLike {
  _id?: string;
  id?: string;
  name?: string;
  status?: string;
  publicationDates?: string[];
}

interface CampaignHistoryUserCampaignLike {
  campaignId?: string;
}

/** Cross-reference a user's campaign enrollments against the campaign catalog to build history items. */
export function buildCampaignHistoryItems(
  userCampaigns: CampaignHistoryUserCampaignLike[] | undefined,
  campaigns: CampaignHistoryCampaignLike[],
): CampaignHistoryItem[] {
  return (userCampaigns ?? [])
    .map((uc) => {
      const camp = campaigns.find((c) => (c._id ?? c.id) === uc.campaignId);
      if (!camp) return null;
      return {
        id: camp._id ?? camp.id ?? uc.campaignId ?? '',
        name: camp.name ?? uc.campaignId ?? '',
        status: camp.status,
        publicationDates: camp.publicationDates,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
}

function calcDurLabel(dates?: string[]): string {
  const valid = dates?.filter((d): d is string => typeof d === 'string' && d.length >= 10);
  if (!valid || valid.length < 2) return '';
  const ms = new Date(valid[valid.length - 1]).getTime() - new Date(valid[0]).getTime();
  if (Number.isNaN(ms) || ms <= 0) return '';
  return `(${Math.round(ms / 86_400_000)}d)`;
}

/** "23일 전" / "6개월 전" / "3년 전" / "Just now" — bilingual relative date. */
function calcAgoLabel(dates: string[] | undefined, t: (en: string, ko: string) => string): string {
  const valid = dates?.filter((d): d is string => typeof d === 'string' && d.length >= 10);
  const last = valid?.[valid.length - 1];
  if (!last) return '';
  const diff = Date.now() - new Date(last).getTime();
  if (!Number.isFinite(diff) || diff < 0) return '';
  const days = Math.floor(diff / 86_400_000);
  if (days < 1) return t('just now', '오늘');
  if (days < 30) return `${days}${t('d ago', '일 전')}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}${t('mo ago', '개월 전')}`;
  const years = Math.floor(months / 12);
  return `${years}${t('y ago', '년 전')}`;
}

interface CampaignHistoryPopoverRenderTriggerCtx {
  open: boolean;
  loading: boolean;
  toggle: () => void;
}

interface CampaignHistoryPopoverProps {
  /** Provide when items are already available (e.g. already loaded on the page) — no fetch. */
  items?: CampaignHistoryItem[];
  /** Header identity to pair with `items` — ignored when using `onLoadItems`, which
   *  resolves its own (see `loadCampaignHistoryItems`'s return shape). */
  channel?: CampaignHistoryChannelInfo;
  /** Provide instead of `items` to fetch lazily the first time the popover opens.
   *  The result is cached for the lifetime of this component instance. On failure,
   *  the popover closes and an error snackbar is shown. */
  onLoadItems?: () => Promise<{ items: CampaignHistoryItem[]; channel?: CampaignHistoryChannelInfo }>;
  triggerLabel?: React.ReactNode;
  /** Extra class appended to the outer wrapper div (e.g. layout modifier) */
  className?: string;
  /** Tooltip shown on the trigger button (hidden while the popover is open). */
  triggerTooltip?: React.ReactNode;
  /** Side the trigger tooltip pops toward. Defaults to 'top'; the channel-name
   *  quick-button rows pass 'bottom' so the tooltip drops below the row. */
  triggerTooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  /** Render the trigger as a square icon button (matching the other channel-links
   *  quick buttons) with `triggerLabel` shown as a small badge in its top-right
   *  corner, instead of the default pill button with an inline text label. */
  iconVariant?: boolean;
  /** Icon button size when `iconVariant` is set — 'sm' (20px, default) or 'md' (26px) to
   *  match whichever size the page's other quick-action buttons use. */
  iconSize?: 'sm' | 'md';
  /** Fully custom trigger (e.g. ChannelLinksPopover's checkbox + label row) — overrides
   *  the built-in pill/icon trigger. Receives the open/loading state and a toggle handler. */
  renderTrigger?: (ctx: CampaignHistoryPopoverRenderTriggerCtx) => React.ReactNode;
}

/** Above ChannelLinksPopover's `.clp-float` (1300) so this can nest inside it as a
 *  sub-popover (e.g. the "Campaign History" action row) without being hidden behind it. */
const POPOVER_Z_INDEX = 1400;

export function CampaignHistoryPopover({
  items: providedItems,
  channel: providedChannel,
  onLoadItems,
  triggerLabel,
  className,
  triggerTooltip,
  triggerTooltipSide = 'top',
  iconVariant = false,
  iconSize = 'sm',
  renderTrigger,
}: CampaignHistoryPopoverProps) {
  const { t } = useI18nText();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState<{ items: CampaignHistoryItem[]; channel?: CampaignHistoryChannelInfo } | null>(null);
  const [loading, setLoading] = useState(false);
  // Guards re-entrancy for the lazy-load effect below. Must be a ref, not state: putting
  // `loading` itself in that effect's deps caused it to tear down and rebuild as soon as
  // `setLoading(true)` fired, marking the in-flight request `cancelled` before it resolved
  // and leaving the popover stuck on "Loading...".
  const fetchingRef = useRef(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });
  // Growing/shrinking the popover via the corner handle already gets repositioned for free —
  // the ResizeObserver below already watches popoverRef and recomputes top/left on any size
  // change, dragging included.
  const { size: resizeSize, onResizeStart } = useCornerResize(popoverRef);

  const items = providedItems ?? loaded?.items ?? [];
  const channel = providedChannel ?? loaded?.channel;

  const POPOVER_MAX_HEIGHT = 400;
  const POPOVER_MARGIN = 8;

  const computePosition = useCallback((): React.CSSProperties => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return { visibility: 'hidden' };
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const popH = Math.min(popoverRef.current?.offsetHeight ?? POPOVER_MAX_HEIGHT, POPOVER_MAX_HEIGHT);
    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const placeAbove = spaceBelow < popH + POPOVER_MARGIN && spaceAbove > spaceBelow;
    const top = placeAbove
      ? Math.max(POPOVER_MARGIN, rect.top - popH - POPOVER_MARGIN)
      : rect.bottom + 4;
    // Horizontal: align left of trigger, but keep within viewport.
    const popW = popoverRef.current?.offsetWidth ?? 360;
    let left = rect.left;
    if (left + popW > vw - POPOVER_MARGIN) left = Math.max(POPOVER_MARGIN, vw - popW - POPOVER_MARGIN);
    if (left < POPOVER_MARGIN) left = POPOVER_MARGIN;
    return { position: 'fixed', top, left, zIndex: POPOVER_Z_INDEX, visibility: 'visible' };
  }, []);

  // Initial measurement: render hidden, measure, then position.
  useLayoutEffect(() => {
    if (!open) { setPopoverStyle({ visibility: 'hidden' }); return; }
    setPopoverStyle({ position: 'fixed', top: 0, left: 0, zIndex: POPOVER_Z_INDEX, visibility: 'hidden' });
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    setPopoverStyle(computePosition());
  }, [open, computePosition, items.length, loading]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setPopoverStyle(computePosition());
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, computePosition]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Reposition whenever the popover's rendered size changes (e.g. a status filter
  // toggle changes the list height, or the loading spinner is replaced by the list) —
  // decouples this component from the filter state, which lives in CampaignHistoryListContent.
  useEffect(() => {
    if (!open || !popoverRef.current) return;
    const el = popoverRef.current;
    const ro = new ResizeObserver(() => setPopoverStyle(computePosition()));
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, computePosition]);

  // Lazy-load on first open when the caller didn't already supply `items`. Cached in
  // `loaded` for the lifetime of this component instance (no refetch on reopen).
  useEffect(() => {
    if (!open || providedItems || !onLoadItems || loaded !== null || fetchingRef.current) return;
    fetchingRef.current = true;
    let cancelled = false;
    setLoading(true);
    onLoadItems()
      .then((res) => { if (!cancelled) setLoaded(res); })
      .catch(() => {
        if (cancelled) return;
        setOpen(false);
        dispatch(showSnackbar({
          text: t('Failed to load campaign history', '캠페인 이력 로드 실패'),
          kind: 'api-error',
          timestamp: Date.now(),
        }));
      })
      .finally(() => {
        fetchingRef.current = false;
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, providedItems, onLoadItems, loaded, dispatch, t]);

  const toggle = useCallback(() => setOpen((p) => !p), []);

  return (
    <div className={`campaign-history-wrap${className ? ` ${className}` : ''}`} ref={wrapRef}>
      {renderTrigger ? renderTrigger({ open, loading, toggle }) : (
        <Tooltip content={open ? null : triggerTooltip} side={triggerTooltipSide}>
          {iconVariant ? (
            <button
              type="button"
              className={`${iconSize === 'md' ? 'actions-btn' : 'actions-btn-sm'} actions-btn--history${open ? ' actions-btn--history-open' : ''}`}
              disabled={loading}
              onClick={toggle}
            >
              <i className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-history'}`} />
              {!loading && triggerLabel != null && <span className="actions-btn-badge">{triggerLabel}</span>}
            </button>
          ) : (
            <button
              type="button"
              className={`campaign-history-btn${open ? ' campaign-history-btn--open' : ''}`}
              onClick={toggle}
            >
              <i className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-history'} campaign-history-btn__icon`} />
              <span className="campaign-history-btn__label">{triggerLabel}</span>
            </button>
          )}
        </Tooltip>
      )}
      {open && createPortal(
        <div
          ref={popoverRef}
          className="campaign-history-popover campaign-history-popover--portaled"
          role="dialog"
          style={{
            ...popoverStyle,
            // Only set once the corner handle has actually been dragged — before that,
            // the popover keeps its normal auto-height (capped by its own max-height).
            ...(resizeSize ? { width: resizeSize.width, height: resizeSize.height } : undefined),
          }}
        >
          {/* Identity header — thumbnail + platform badge + name + "History" suffix,
              then a close button flush right. Mirrors EstPriceCell's "Details" popover
              header exactly (same classes) so the two channel-row popovers read as one
              family. Renders even without a resolved channel (e.g. still loading, or a
              caller that only supplied a bare items list) so the close button is always
              reachable without depending on outside-click/Esc. */}
          <div className="est-price-popover-header">
            <ChannelThumbnail src={channel?.thumbnail} />
            <ChannelTypeIcon type={channel?.type ?? 'youtube'} size={14} asBadge />
            <span className="est-price-popover-name">{channel?.name}</span>
            <span className="campaign-history-popover__identity-suffix">{t('History', '이력')}</span>
            <button
              type="button"
              className="est-price-popover-close"
              onClick={() => setOpen(false)}
              aria-label={t('Close', '닫기')}
            >
              <i className="fa fa-times" />
            </button>
          </div>
          {loading ? (
            <div className="campaign-history-popover__loading">
              <i className="fa fa-spinner fa-spin" /> {t('Loading...', '로딩 중...')}
            </div>
          ) : (
            <CampaignHistoryListContent items={items} onItemClick={() => setOpen(false)} />
          )}
          <PopoverResizeHandle onPointerDown={onResizeStart} />
        </div>,
        document.body,
      )}
    </div>
  );
}

export interface CampaignHistoryButtonProps {
  /** Pre-computed via `buildCampaignHistoryItems` from data the host page already has
   *  loaded (its own campaign enrollments + the campaign catalog) — no fetch needed. */
  items: CampaignHistoryItem[];
  channel?: CampaignHistoryChannelInfo;
  /** Square icon size — 'sm' (20px) or 'md' (26px, default) — match whichever size the
   *  sibling quick-action buttons (chat/details/stats/edit) use on that page. */
  size?: 'sm' | 'md';
  /** Tooltip text — the caller picks the copy (it already knows whether there IS
   *  history, and for InfluencerRow's sibling rows, why there never will be). */
  tooltip: React.ReactNode;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
}

/** Campaign-history icon button for callers that already have a channel's campaign
 *  enrollments + campaign catalog loaded locally (e.g. the influencer dashboard,
 *  communications/candidates/analytics rows) — shows the count badge immediately,
 *  unlike `CampaignHistoryQuickButton`'s on-demand self-fetch (which can't know the
 *  count before the popover is opened). Renders disabled instead of hidden when there's
 *  no history, per CLAUDE.md "숨김 최소화" — a vanished icon reads as "no such feature"
 *  where a greyed-out one reads as "none for this channel". */
export function CampaignHistoryButton({ items, channel, size = 'md', tooltip, tooltipSide = 'bottom' }: CampaignHistoryButtonProps) {
  const { t } = useI18nText();
  if (items.length === 0) {
    return (
      <Tooltip content={tooltip} side={tooltipSide}>
        <button
          type="button"
          className={`${size === 'md' ? 'actions-btn' : 'actions-btn-sm'} actions-btn--history actions-btn--history-empty`}
          disabled
          aria-label={t('No campaign history', '참여된 캠페인 이력 없음')}
        >
          <i className="fa fa-history" />
        </button>
      </Tooltip>
    );
  }
  return (
    <CampaignHistoryPopover
      iconVariant
      iconSize={size}
      triggerLabel={items.length}
      triggerTooltip={tooltip}
      triggerTooltipSide={tooltipSide}
      items={items}
      channel={channel}
    />
  );
}

interface CampaignHistoryListContentProps {
  items: CampaignHistoryItem[];
  /** Called when a history entry (linking to the campaign editor) is clicked. */
  onItemClick?: () => void;
}

/** Most-recently-published first; items with no publication date sort last. Applied
 *  separately to the campaign and roster groups so a roster with no date doesn't get
 *  buried behind campaigns, and vice versa. */
function sortByLatestPublication<T extends { publicationDates?: string[] }>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    const filteredA = a.publicationDates?.filter((d): d is string => d != null && d.length >= 10);
    const filteredB = b.publicationDates?.filter((d): d is string => d != null && d.length >= 10);
    const dA = filteredA?.[filteredA.length - 1] ?? '';
    const dB = filteredB?.[filteredB.length - 1] ?? '';
    return dB.localeCompare(dA);
  });
}

/** Date column content shared by the campaign list rows and the roster table rows —
 *  relative "N일 전" tag + absolute range + duration, or an italic "no date" fallback. */
function CampaignHistoryDateCell({ item, t }: { item: CampaignHistoryItem; t: (en: string, ko: string) => string }) {
  const dateStr = formatCampaignDateRange(item.publicationDates);
  if (!dateStr) {
    return (
      <span className="campaign-history-popover__dates campaign-history-popover__dates--empty">
        {t('No publication date', '게시 날짜 없음')}
      </span>
    );
  }
  const durLabel = calcDurLabel(item.publicationDates);
  const agoLabel = calcAgoLabel(item.publicationDates, t);
  return (
    <span className="campaign-history-popover__dates">
      {agoLabel && <span className="campaign-history-popover__ago-tag">{agoLabel}</span>}
      <i className="fa fa-calendar" />
      {dateStr}
      {durLabel && <span className="campaign-history-popover__dur">{durLabel}</span>}
    </span>
  );
}

/** Name cell shared by both sections — a link to the campaign/roster editor, or a
 *  plain (optionally tooltipped) span for unmatched entries that can't be opened. */
function CampaignHistoryNameLink({
  item,
  className,
  onItemClick,
  children,
}: {
  item: CampaignHistoryItem;
  className: string;
  onItemClick?: () => void;
  children: React.ReactNode;
}) {
  if (item.unmatched) {
    const unmatchedClass = `${className} campaign-history-popover__link--unmatched`;
    return item.unmatchedTooltip ? (
      <Tooltip content={item.unmatchedTooltip}>
        <span className={unmatchedClass}>{children}</span>
      </Tooltip>
    ) : (
      <span className={unmatchedClass}>{children}</span>
    );
  }
  return (
    <Link
      to={`/campaigns/details/${encodeURIComponent(item.id)}`}
      className={className}
      onClick={() => onItemClick?.()}
    >
      {children}
    </Link>
  );
}

/** Same ordering as the campaign dashboard's status pills (src/pages/campaigns/index.tsx) —
 *  in-progress → draft → private_draft first, then completed/template/roster/trashed. */
const STATUS_COUNT_DISPLAY_PRIORITY: Record<string, number> = {
  in_progress: 0,
  draft: 1,
  private_draft: 2,
  completed: 10,
  template: 11,
  roster: 12,
  trashed: 13,
};

/** Bilingual label, only for the tooltip text — mirrors the dashboard's local
 *  `useStatusMeta()` (not exported, so re-declared here) rather than the plain
 *  English-only `CAMPAIGN_STATUS_LABEL`. */
function statusChipLabel(status: string, t: (en: string, ko: string) => string): string {
  const labels: Record<string, [string, string]> = {
    roster: ['Roster', '로스터'],
    template: ['Template', '템플릿'],
    private_draft: ['Private draft', '비공개 초안'],
    draft: ['Draft', '초안'],
    in_progress: ['In progress', '진행 중'],
    completed: ['Completed', '완료'],
    trashed: ['Trashed', '삭제됨'],
  };
  const [en, ko] = labels[status] ?? [status.replace(/_/g, ' '), status.replace(/_/g, ' ')];
  return t(en, ko);
}

/** Header (title + count + status filter) and scrollable list rendered inside
 *  CampaignHistoryPopover's anchored popover (with or without lazy loading).
 *  Campaigns and rosters are split into two sections — a roster shares no
 *  status with any real campaign, so mixing them into one numbered list read
 *  as one undifferentiated pile (see conversation: rosters like "NightMedia"
 *  interleaved with actual campaigns under one "Campaign history" heading). */
function CampaignHistoryListContent({ items, onItemClick }: CampaignHistoryListContentProps) {
  const { t } = useI18nText();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const campaignItems = useMemo(() => items.filter((it) => it.status !== 'roster'), [items]);
  const rosterItems = useMemo(() => items.filter((it) => it.status === 'roster'), [items]);

  // Sorted the same way as the campaign dashboard's own status pills (priority,
  // then descending count) — see STATUS_COUNT_DISPLAY_PRIORITY above.
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const it of campaignItems) {
      const s = it.status ?? '';
      if (s) counts[s] = (counts[s] ?? 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => {
      const pa = STATUS_COUNT_DISPLAY_PRIORITY[a[0]] ?? 100;
      const pb = STATUS_COUNT_DISPLAY_PRIORITY[b[0]] ?? 100;
      if (pa !== pb) return pa - pb;
      return b[1] - a[1];
    });
  }, [campaignItems]);

  const sortedCampaigns = useMemo(() => sortByLatestPublication(campaignItems), [campaignItems]);
  const sortedRosters = useMemo(() => sortByLatestPublication(rosterItems), [rosterItems]);

  const visibleCampaigns = sortedCampaigns.filter((item) => !statusFilter || item.status === statusFilter);

  return (
    <div className="est-price-popover-body">
      {/* ── Campaign section — same ggc-table-frame + gc-table--compact shell as
          EstPriceCell's "Details" popover tables, with the status filter where
          that popover puts its "Edit" action (title left, action right). ── */}
      <div className="est-price-popover-section">
        <div className="est-price-popover-section-head">
          <h4 className="ggc-text-section-header">
            {t('Campaign', '캠페인')}
            <span className="campaign-history-popover__count">({campaignItems.length})</span>
          </h4>
          {statusCounts.length > 0 && (
            // Same chip look as the campaign dashboard's own status filter
            // (.campaign-status-chip--filter) — see src/pages/campaigns/index.tsx.
            <span className="status-counts">
              {statusCounts.map(([status, count]) => {
                const selected = statusFilter === status;
                const dotClass = CAMPAIGN_STATUS_DOT_CLASS[status] ?? 'bg-ggc-border-gray';
                const clickHint = selected
                  ? t('Click to clear status filter', '클릭하여 상태 필터 해제')
                  : t('Filter by this status', '이 상태만 보기');
                const tooltipContent = `${statusChipLabel(status, t)} ${count} — ${clickHint}`;
                return (
                  <Tooltip essential key={status} content={tooltipContent}>
                    <button
                      type="button"
                      className={`campaign-status-chip campaign-status-chip--filter campaign-status-chip--${status}${selected ? ' campaign-status-chip--selected' : ''}`}
                      onClick={() => setStatusFilter((prev) => (prev === status ? null : status))}
                      aria-pressed={selected}
                    >
                      <span className="campaign-status-chip__label">
                        <span className={`inline-block size-[7px] rounded-full shrink-0 ${dotClass}`} />
                      </span>
                      <span className="campaign-status-chip__sep" />
                      <span className="campaign-status-chip__count">{count}</span>
                    </button>
                  </Tooltip>
                );
              })}
            </span>
          )}
        </div>
        <div className="est-price-popover-section-body">
          <div className="ggc-table-frame">
            <table className="gc-table gc-table--compact">
              <thead>
                <tr>
                  <th className="text-right w-[28px]">#</th>
                  <th>{t('Campaign', '캠페인')}</th>
                  <th>{t('Date', '날짜')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleCampaigns.map((item, idx) => (
                  <tr key={item.id}>
                    <td className="text-right text-ggc-disabled">{idx + 1}</td>
                    <td>
                      <CampaignHistoryNameLink item={item} className="campaign-history-popover__table-name" onItemClick={onItemClick}>
                        <CampaignStatusIcon status={item.status} />
                        <Tooltip content={item.name}>
                          <span className="campaign-history-popover__name">{item.name}</span>
                        </Tooltip>
                      </CampaignHistoryNameLink>
                    </td>
                    <td>
                      <CampaignHistoryDateCell item={item} t={t} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Roster section — same table shell, own title. A roster shares no
          status with a real campaign, so it never belongs inside the table
          above (see conversation: rosters like "NightMedia" used to be
          interleaved with actual campaigns under one flat list). Its rows get
          the same status-icon prefix as the Campaign table above (even though
          every row here is the same 'roster' status) purely so both tables
          share one row shape. */}
      {rosterItems.length > 0 && (
        <div className="est-price-popover-section">
          <div className="est-price-popover-section-head">
            <h4 className="ggc-text-section-header">
              {t('Roster', '로스터')}
              <span className="campaign-history-popover__count">({rosterItems.length})</span>
            </h4>
          </div>
          <div className="est-price-popover-section-body">
            <div className="ggc-table-frame">
              <table className="gc-table gc-table--compact">
                <thead>
                  <tr>
                    <th className="text-right w-[28px]">#</th>
                    <th>{t('Roster', '로스터')}</th>
                    <th>{t('Date', '날짜')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRosters.map((item, idx) => (
                    <tr key={item.id}>
                      <td className="text-right text-ggc-disabled">{idx + 1}</td>
                      <td>
                        <CampaignHistoryNameLink item={item} className="campaign-history-popover__table-name" onItemClick={onItemClick}>
                          <CampaignStatusIcon status={item.status} />
                          <Tooltip content={item.name}>
                            <span className="campaign-history-popover__name">{item.name}</span>
                          </Tooltip>
                        </CampaignHistoryNameLink>
                      </td>
                      <td>
                        <CampaignHistoryDateCell item={item} t={t} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Fetches a user's campaign history on demand (userId → items + channel identity),
 *  shared by every self-fetching caller (CampaignHistoryQuickButton, ChannelLinksPopover's
 *  action row). Channel identity (thumbnail/platform/name) piggybacks on the same
 *  `loadPanelUser` call already needed for the campaign enrollments — no extra fetch. */
export async function loadCampaignHistoryItems(
  userId: string,
): Promise<{ items: CampaignHistoryItem[]; channel?: CampaignHistoryChannelInfo }> {
  const [userRes, campaigns] = await Promise.all([
    loadPanelUser<{
      user: {
        campaigns?: Array<{ campaignId?: string }>;
        channels?: UserChannel[];
        profile?: { fullname?: string; name?: string };
        thumbnail?: string;
      };
    }>(userId),
    getCampaigns(),
  ]);
  const ch = userRes.user.channels?.[0];
  return {
    items: buildCampaignHistoryItems(userRes.user.campaigns, campaigns),
    channel: {
      thumbnail: ch?.thumbnail ?? userRes.user.thumbnail,
      type: ch?.type,
      name: userRes.user.profile?.fullname || userRes.user.profile?.name || ch?.title,
    },
  };
}

export interface CampaignHistoryQuickButtonProps {
  userId: string;
  /** Square icon size — 'sm' (20px) or 'md' (26px) — match whichever size the
   *  sibling quick-action buttons (chat/details/stats/edit) use on that page. */
  size?: 'sm' | 'md';
  tooltip?: React.ReactNode;
  /** Side the trigger tooltip pops toward (defaults to 'top'). */
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
}

/** Self-fetching "Campaign History" quick button for pages that render the channel-links
 *  quick-action row (chat/details/stats/edit) but don't already have the user's campaign
 *  enrollments + campaign catalog loaded locally (unlike the influencer dashboard, which
 *  passes pre-built items straight to CampaignHistoryPopover). Loads on first open — mirrors
 *  the "Edit Channel" quick button's on-demand fetch — and opens as an anchored popover. */
export function CampaignHistoryQuickButton({ userId, size = 'sm', tooltip, tooltipSide }: CampaignHistoryQuickButtonProps) {
  const { t } = useI18nText();
  return (
    <CampaignHistoryPopover
      iconVariant
      iconSize={size}
      triggerTooltip={tooltip ?? t('"Campaign History" as a popup', '"캠페인 이력" 팝업으로 보기')}
      triggerTooltipSide={tooltipSide}
      onLoadItems={() => loadCampaignHistoryItems(userId)}
    />
  );
}
